// ─── RELEVÉ d'un compte : les opérations, mois par mois ───────────
// Calque plein écran ouvert en tapant un compte. C'est le pendant d'EcranBudget
// côté COMPTE : là-bas « à quoi sert l'argent », ici « ce qui a bougé sur le
// compte, et combien il reste ».
//
// Par MOIS et non par jour (choix de Cédric) : un relevé se lit par mois, et le
// solde de fin de mois est le chiffre qu'on compare à celui de la banque.
//
// Disposition (revue le 10/08 sur retour de Cédric) : solde → recherche → mois
// en EN-TÊTE FIXE, et le défilement ne porte que sur les opérations. Avant,
// tout défilait ensemble : dès qu'on descendait dans la liste, on perdait de
// vue le solde et le mois auxquels ces lignes se rapportent.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran, frJMA,
  useUI, useT, confirmerSuppression,
} from '@mop/core';
import { formatEuro, montantOperation } from './services/calculs';
import { moisCourant, decalerMois } from './services/occurrences';
import {
  releveDuMois, totauxReleve, soldeFinDeMois, moisVoisinAvecOperations, dateOp,
  dernierMoisAvecOperations, finDeMois,
} from './services/releveCompte';
import { filtrerOperations, nbFiltresReleve } from './services/filtres';
import { etatOperation, compterEtats } from './services/etatRattachement';
import { emojiCompte } from './services/comptes';
import { emojiCategorie, cleCategorie } from './data/categories';
import { ArbreEnveloppes } from './FeuilleArbreEnveloppes';
import FeuilleFiltresCompte from './FeuilleFiltresCompte';
import BarreRecherche from './BarreRecherche';
import { prefSession, definirPrefSession } from './services/prefsSession';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const libelleMois = (mk, t) => {
  const [y, m] = String(mk).split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk} ${y}`;
};

// L'état de rattachement, en une icône (Cédric, 11/08 : « savoir rapidement si
// une opération est déjà rattachée »). Sur un relevé importé, l'emoji de
// CATÉGORIE ne disait rien — toutes les lignes portaient le même 📥. L'état,
// lui, dit ce qui reste à faire. La catégorie se lit dans la fiche.
const ICONE_ETAT = { rattachee: '✅', partielle: '🟡', libre: '⚪', exclue: '⊘' };

export default function EcranCompte({
  compte, operations = [], rattachements = [], lignes = [], budgets = [],
  onFermer, onFiche, onEditer, onSupprimer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [mois, setMois] = useState(moisCourant);
  const [recherche, setRecherche] = useState('');
  // La VUE : releve chronologique, ou par enveloppe (Cedric, 15/08). Une
  // BASCULE en place, pas une modale : on reste sur le compte, on change de
  // lecture — comme le 🧾/📅 de l'onglet Budget.
  const [vueBudget, setVueBudget] = useState(false);
  const [sens, setSens] = useState('tout');
  const [avecHorsCalcul, setAvecHorsCalcul] = useState(true);
  const [tri, setTri] = useState('recente');
  const [feuille, setFeuille] = useState(null);   // onglet ouvert de la feuille
  // La SYNTHÈSE se plie depuis l'en-tête (16/08) — mémorisé pour la session.
  const [syntheseVisible, setSyntheseVisible] = useState(() => prefSession('syntheseReleve', true));
  const basculerSynthese = () => setSyntheseVisible(v => {
    definirPrefSession('syntheseReleve', !v);
    return !v;
  });

  const cptId = compte?.id;
  // Ouvrir sur le dernier mois qui porte des opérations, pas sur le mois
  // courant : le dernier relevé importé date souvent d'avant, et l'écran
  // s'ouvrait alors sur une page vide. Une seule fois — les opérations
  // arrivent par une écoute, et re-caler à chaque envoi interdirait de
  // naviguer.
  const cale = useRef(false);
  useEffect(() => { cale.current = false; }, [cptId]);
  useEffect(() => {
    if (cale.current) return;
    const dernier = dernierMoisAvecOperations(operations, cptId);
    if (!dernier) return;                    // rien encore reçu : on retentera
    cale.current = true;
    if (dernier !== mois) setMois(dernier);
  }, [operations, cptId, mois]);
  // Le mois complet sert aux TOTAUX et au solde ; la recherche ne doit pas les
  // faire varier (un total qui change à chaque lettre tapée ne veut rien dire).
  const duMois = useMemo(
    () => releveDuMois({ operations, compteId: cptId, mk: mois }),
    [operations, cptId, mois],
  );
  const perimetre = useMemo(
    () => filtrerOperations(duMois, { sens, avecHorsCalcul }),
    [duMois, sens, avecHorsCalcul],
  );
  const liste = useMemo(
    () => filtrerOperations(duMois, { recherche, sens, avecHorsCalcul, tri }),
    [duMois, recherche, sens, avecHorsCalcul, tri],
  );
  const totaux = useMemo(() => totauxReleve(perimetre), [perimetre]);
  const solde = useMemo(() => soldeFinDeMois(compte, operations, mois), [compte, operations, mois]);

  // Decompte par etat sur le PERIMETRE du mois : ce qui reste a rattacher.
  const etats = useMemo(() => compterEtats(duMois, rattachements), [duMois, rattachements]);

  const surCourant = mois === moisCourant();
  const nbFiltres = nbFiltresReleve({ sens, avecHorsCalcul });
  // Doubles flèches : sauter au prochain mois qui porte des opérations. Sans
  // elles, un relevé qui commence en 2024 se traverse mois vide par mois vide.
  const avant = moisVoisinAvecOperations(operations, cptId, mois, -1);
  const apres = moisVoisinAvecOperations(operations, cptId, mois, 1);

  return (
    <GesteRetourEcran actif onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={compte?.nom || ''} iconeTitre={emojiCompte(compte)} onRetour={onFermer}>
          {/* Le relevé dit d'OÙ vient l'argent ; l'arbre dit à QUOI il sert.
              Le bouton BASCULE la vue (15/08) — l'ancienne modale obligeait à
              fermer pour revenir. 🌳 mène à l'arbre, 📅 ramène au relevé. */}
          <TouchableOpacity onPress={() => setVueBudget(v => !v)} hitSlop={HIT}
            accessibilityLabel={t('finances.vueParEnveloppe')}>
            <Text style={{ fontSize: 19, marginLeft: 14 }}>{vueBudget ? '📅' : '🌳'}</Text>
          </TouchableOpacity>
          {/* La bascule de SYNTHÈSE (16/08) : grisée quand la carte est cachée. */}
          <TouchableOpacity onPress={basculerSynthese} hitSlop={HIT}
            accessibilityLabel={t('finances.basculerSynthese')}>
            <Text style={{ fontSize: 19, marginLeft: 14, opacity: syntheseVisible ? 1 : 0.35 }}>📊</Text>
          </TouchableOpacity>
        </PageHeader>

        {/* EN-TÊTE FIXE : solde, recherche, mois. Il ne défile pas — descendre
            dans la liste ne doit pas faire perdre de vue à quoi ces lignes se
            rapportent. */}
        <View style={s.entete}>
          {/* Solde à la FIN du mois affiché : le chiffre qu'on compare à celui
              de la banque. La DATE est écrite sous le montant — le solde étant
              désormais au-dessus de la navigation, rien d'autre ne dirait à
              quelle date il se rapporte. */}
          {syntheseVisible && (
          <View style={s.totalCarte}>
            <Text style={s.totalLbl}>{t('finances.soldeFinal')}  ·  {frJMA(finDeMois(mois))}</Text>
            <Text style={[s.totalVal, solde < 0 && { color: UI.danger || '#c0392b' }]}>{formatEuro(solde)}</Text>
            <Text style={s.detail}>
              <Text style={{ color: UI.ok || '#2e7d32' }}>+{formatEuro(totaux.entrees)}</Text>
              {'   '}
              <Text style={{ color: UI.danger || '#c0392b' }}>−{formatEuro(totaux.sorties)}</Text>
              {'   ·   '}{totaux.nb} {t('finances.mouvements')}
              {totaux.provisions > 0 ? `   ·   ⏳ ${formatEuro(totaux.provisions)}` : ''}
            </Text>
            {/* Ce qui reste à rattacher, en clair : c'est le travail qui
                attend, et il ne se devine pas en comptant les ⚪ à l'œil. */}
            {etats.libre + etats.partielle > 0 && (
              <Text style={s.aFaire}>
                ⚪ {etats.libre}{etats.partielle > 0 ? `   🟡 ${etats.partielle}` : ''}
                {'   ·   '}{t('finances.aRattacher')}
              </Text>
            )}
          </View>
          )}

          {/* Le modèle commun : 🔽 filtre · champ · ↕ tri (15/08). */}
          <BarreRecherche valeur={recherche} onChange={setRecherche}
            nbFiltres={nbFiltres} onFiltres={() => setFeuille('filtres')}
            triActif={tri !== 'recente'} onTri={() => setFeuille('tri')} />

          {/* Navigation entre les mois — même forme que la vue Mois : 🎯 collé
              au titre, grisé quand on y est déjà, pour que rien ne bouge.
              Les doubles flèches sautent au mois voisin qui porte vraiment des
              opérations. */}
          <View style={s.barre}>
            <TouchableOpacity onPress={() => avant && setMois(avant)} hitSlop={HIT} disabled={!avant}>
              <Text style={[s.nav, !avant && s.navOff]}>«</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setMois(decalerMois(mois, -1))} hitSlop={HIT}>
              <Text style={s.nav}>‹</Text>
            </TouchableOpacity>
            <View style={s.titreZone}>
              <Text style={s.mois}>{libelleMois(mois, t)}</Text>
              <TouchableOpacity onPress={() => setMois(moisCourant())} hitSlop={HIT} disabled={surCourant}>
                <Text style={[s.cible, surCourant && s.navOff]}>🎯</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity onPress={() => setMois(decalerMois(mois, 1))} hitSlop={HIT}>
              <Text style={s.nav}>›</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => apres && setMois(apres)} hitSlop={HIT} disabled={!apres}>
              <Text style={[s.nav, !apres && s.navOff]}>»</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Seule zone défilante : les opérations. */}
        <ScrollView style={{ flex: 1 }} contentContainerStyle={s.corps}>
          {/* En vue BUDGET, l'arbre par enveloppe remplace la liste — le
              solde, la recherche et le mois restent : on change de lecture,
              pas d'écran. Les opérations passées à l'arbre sont celles DU
              MOIS affiché, comme l'ancienne modale. */}
          {vueBudget ? (
            <ArbreEnveloppes operations={duMois} rattachements={rattachements}
              lignes={lignes} budgets={budgets} onFiche={onFiche}
              onEditer={onEditer} onSupprimer={onSupprimer} />
          ) : (
          <>
          {liste.length === 0 ? (
            <EmptyState icon="🧾" texte={t('finances.aucuneOpPeriode', { periode: libelleMois(mois, t) })} />
          ) : liste.map(o => (
            <LigneSwipe key={o.id} espacement={8} enabled
              onEditer={onEditer ? () => onEditer(o) : undefined}
              onSupprimer={onSupprimer ? () => confirmerSuppression(
                t('commun.supprimer'), o.libelle || formatEuro(o.montant),
                () => onSupprimer(o)) : undefined}>
              {/* Tap = FICHE en lecture, jamais l'édition directe : c'est le
                  principe app-wide (AGENTS.md), que cet écran enfreignait
                  depuis 0.6.4.0. L'édition reste au swipe →. */}
              <TouchableOpacity style={s.carte} activeOpacity={onFiche ? 0.7 : 1}
                onPress={onFiche ? () => onFiche(o) : undefined}>
                <Text style={{ fontSize: 17 }}>{ICONE_ETAT[etatOperation(o, rattachements)]}</Text>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={s.libelle} numberOfLines={1}>
                    {o.libelle || t(cleCategorie(o.categorie))}
                  </Text>
                  <Text style={s.sous} numberOfLines={1}>
                    {frJMA(dateOp(o))}
                    {o.paye === false ? '  ·  ⏳' : ''}
                    {o.exclu ? '  ·  ⊘' : ''}
                  </Text>
                </View>
                <Text style={[s.montant, o.sens === 'revenu' && { color: UI.ok || '#2e7d32' },
                  (o.exclu || o.paye === false) && { opacity: 0.45 }]}>
                  {montantOperation(o.montant, o.sens)}
                </Text>
              </TouchableOpacity>
            </LigneSwipe>
          ))}
          </>
          )}
        </ScrollView>

        <FeuilleFiltresCompte visible={!!feuille} ongletInitial={feuille || 'filtres'}
          sens={sens} avecHorsCalcul={avecHorsCalcul} tri={tri}
          onAppliquer={({ sens: se, avecHorsCalcul: h, tri: x }) => { setSens(se); setAvecHorsCalcul(h); setTri(x); }}
          onFermer={() => setFeuille(null)} />
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  // L'en-tête est fixe : une bordure basse marque la limite avec la zone qui
  // défile, sinon les lignes semblent glisser sous rien.
  entete: { paddingHorizontal: 12, paddingTop: 12, paddingBottom: 4, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  corps: { padding: 12, paddingBottom: 30 },
  barre: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 8 },
  titreZone: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  mois: { fontSize: 16, fontWeight: '800', color: UI.ink },
  cible: { fontSize: 15 },
  nav: { fontSize: 24, color: UI.accent, paddingHorizontal: 4 },
  navOff: { opacity: 0.25 },
  totalCarte: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 12 },
  totalLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  totalVal: { fontSize: 21, fontWeight: '800', color: UI.ink, marginTop: 2 },
  detail: { fontSize: 12, color: UI.muted, marginTop: 5, fontWeight: '700' },
  aFaire: { fontSize: 12, color: UI.faint, marginTop: 4, fontWeight: '700' },
  controles: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  btnIc: { width: 42, height: 42, borderRadius: 11, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card },
  btnIcOn: { borderColor: UI.accent, borderWidth: 1.5 },
  ic: { fontSize: 17 },
  pastille: { position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, borderRadius: 999, paddingHorizontal: 4, backgroundColor: UI.accent, alignItems: 'center', justifyContent: 'center' },
  pastilleTxt: { fontSize: 10.5, color: '#fff', fontWeight: '800' },
  rechercheZone: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12 },
  recherche: { flex: 1, paddingVertical: 10, fontSize: 14, color: UI.ink },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 11, backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 12 },
  libelle: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 1 },
  montant: { fontSize: 15, fontWeight: '800', color: UI.ink },
});
